import { CHAIN_COLORS, fmtUsd, fmtPrice } from "@/lib/api";
import Sparkline from "@/components/Sparkline";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CheckCircle, XCircle, TrendUp, TrendDown, Lightning } from "@phosphor-icons/react";

function ConfidenceBar({ value }) {
  const color = value >= 70 ? "#22c55e" : value >= 50 ? "#eab308" : "#ef4444";
  return (
    <div className="flex items-center gap-2">
      <div className="w-14 h-1.5 bg-[#27272a]">
        <div className="h-full" style={{ width: `${value}%`, backgroundColor: color }} />
      </div>
      <span className="font-mono text-xs" style={{ color }}>{value}</span>
    </div>
  );
}

function ReasonList({ reasons }) {
  return (
    <div className="flex flex-col gap-1 max-w-xs">
      {(reasons || []).map((r, i) => (
        <div key={i} className="flex items-start gap-1.5 text-xs">
          {r.ok ? <CheckCircle size={13} className="text-green-500 mt-0.5 shrink-0" weight="fill" />
            : <XCircle size={13} className="text-red-500 mt-0.5 shrink-0" weight="fill" />}
          <span className="text-[#f4f4f5]">{r.label}: <span className="text-[#a1a1aa]">{r.detail}</span></span>
        </div>
      ))}
    </div>
  );
}

export default function CenterPanel({ candidates, onTrade, tradingAddr, onLive, solConfigured }) {
  return (
    <section className="col-span-12 lg:col-span-7 border-r border-[#27272a] overflow-y-auto p-4 flex flex-col gap-3" data-testid="center-panel">
      <div className="flex items-center justify-between">
        <h2 className="font-head text-base font-semibold tracking-tight flex items-center gap-2">
          <Lightning weight="fill" size={16} className="text-cyan-400" /> Ranked Token Candidates
        </h2>
        <span className="text-xs font-mono text-[#a1a1aa]" data-testid="candidate-count">{candidates.length} tokens</span>
      </div>

      <div className="border border-[#27272a]">
        <div className="grid grid-cols-[1.6fr_0.8fr_0.9fr_0.9fr_1fr_0.9fr_0.7fr] gap-2 px-3 py-2 border-b border-[#27272a] bg-[#18181b] text-[9px] uppercase tracking-wider text-[#a1a1aa] font-medium sticky top-0">
          <span>Token</span><span>Chain</span><span className="text-right">Liquidity</span>
          <span className="text-right">Mkt Cap</span><span>Confidence</span><span>Chart</span><span className="text-right">Action</span>
        </div>
        <TooltipProvider delayDuration={100}>
          {candidates.length === 0 && (
            <div className="px-3 py-10 text-center text-sm text-[#52525b] font-mono">
              Collecting callouts… signals appear within a cycle (~45s).
            </div>
          )}
          {candidates.map((c) => {
            const up = c.price_change_24h >= 0;
            const busy = tradingAddr === c.address;
            return (
              <div key={`${c.chain}-${c.address}`}
                data-testid={`token-row-${c.symbol}`}
                className={`grid grid-cols-[1.6fr_0.8fr_0.9fr_0.9fr_1fr_0.9fr_0.7fr] gap-2 px-3 py-2.5 border-b border-[#1f1f22] items-center hover:bg-[#18181b] transition-colors ${c.passed ? "" : "opacity-60"}`}>
                <div className="flex flex-col min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-sm font-semibold truncate">{c.symbol}</span>
                    {c.passed
                      ? <CheckCircle size={13} weight="fill" className="text-green-500 shrink-0" />
                      : <XCircle size={13} weight="fill" className="text-red-500 shrink-0" />}
                  </div>
                  <span className="text-[10px] text-[#a1a1aa] truncate">{c.name}</span>
                  <span className="text-[9px] text-[#52525b] font-mono">{c.callouts_30m} callouts/30m</span>
                </div>

                <div>
                  <span className="text-[10px] font-mono px-1.5 py-0.5 border"
                    style={{ borderColor: (CHAIN_COLORS[c.chain] || "#3f3f46") + "66", color: CHAIN_COLORS[c.chain] || "#a1a1aa" }}>
                    {c.chain}
                  </span>
                </div>

                <div className="text-right font-mono text-xs">{fmtUsd(c.liquidity)}</div>
                <div className="text-right font-mono text-xs">{fmtUsd(c.market_cap)}</div>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="cursor-help"><ConfidenceBar value={c.confidence} /></div>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="bg-[#09090b] border-[#3f3f46] rounded-none p-3">
                    <ReasonList reasons={c.reasons} />
                  </TooltipContent>
                </Tooltip>

                <div className="flex flex-col">
                  <Sparkline history={c.price_history} up={up} />
                  <span className={`text-[10px] font-mono flex items-center gap-0.5 ${up ? "text-green-400" : "text-red-400"}`}>
                    {up ? <TrendUp size={10} /> : <TrendDown size={10} />}{c.price_change_24h?.toFixed(1)}%
                  </span>
                </div>

                <div className="text-right flex flex-col items-end gap-1">
                  <Button data-testid={`trade-btn-${c.symbol}`} size="sm" disabled={busy || !c.passed || c.price <= 0}
                    onClick={() => onTrade(c)}
                    className="rounded-none h-7 px-2.5 bg-cyan-500 hover:bg-cyan-400 text-black text-xs font-semibold active:scale-95 disabled:opacity-30 disabled:bg-[#27272a] disabled:text-[#52525b]">
                    {busy ? "..." : "Paper"}
                  </Button>
                  {c.chain === "Solana" && (
                    <Button data-testid={`live-btn-${c.symbol}`} size="sm" disabled={!c.passed || c.price <= 0}
                      onClick={() => onLive(c)}
                      title={!solConfigured ? "Live wallet not configured" : (!c.passed ? "Token hasn't passed risk filters" : "Execute real on-chain trade")}
                      className={`rounded-none h-7 px-2.5 text-xs font-semibold active:scale-95 disabled:opacity-30 disabled:bg-[#27272a] disabled:text-[#52525b] ${solConfigured ? "bg-red-600 hover:bg-red-500 text-white" : "bg-transparent border border-[#3f3f46] text-[#a1a1aa] hover:bg-[#27272a]"}`}>
                      Live
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </TooltipProvider>
      </div>
    </section>
  );
}
